import React from 'react';

import DesktopExpandedKeg from './components/DesktopExpandedKeg/DesktopExpandedKeg';
import MobileExpandedKeg from './components/MobileExpandedKeg/MobileExpandedKeg';
import * as styles from './ExpandedKeg.module.scss';
import { InvestmentType } from './expandedKegResources';

type Props = {
  investmentTemplateData: Queries.InvestmentTemplateFragment['investmentPage'];
};

const ExpandedKeg: React.FC<Props> = ({ investmentTemplateData }) => {
  const expandedKegData = [
    {
      investmentType: 'keep',
      kegIconPath: 'icons/keep',
      subHeader:
        'Yield-focused cash management. Compelling rates, shortest terms.',
      description: (
        <>
          These diversified notes provide compelling yield, and a flexible way
          to manage cash.
          <br />
          <br />
          Investors can roll over capital into the next series for compounding
          interest or easily reallocate to other EquityMultiple investments.
        </>
      ),
      mobileDescription:
        'Best for investors looking for a short-term alternative for their savings.',
      mainImage: investmentTemplateData?.keepImage,
      mainImageDescription: {
        offeringName: investmentTemplateData?.keepInvestmentTitle || '',
        offeringLink: investmentTemplateData.keepLink || '',
        metric1: {
          label: investmentTemplateData?.keepInvestmentMetric1Label || '',
          value: investmentTemplateData?.keepInvestmentMetric1Value || '',
        },
        metric2: {
          label: investmentTemplateData?.keepInvestmentMetric2Label || '',
          value: investmentTemplateData?.keepInvestmentMetric2Value || '',
        },
        metric3: {
          label: investmentTemplateData?.keepInvestmentMetric3Label || '',
          value: investmentTemplateData?.keepInvestmentMetric3Value || '',
        },
      },
    },
    {
      investmentType: 'earn',
      kegIconPath: 'icons/earn',
      subHeader:
        'CRE investments offering current yield, payment priority, and a relatively short term. An income focus.',
      listItems: [
        'Senior debt — target returns of 8-12%',
        'Preferred equity — target preferred returns of 10-14%',
        'Yield-focused funds',
      ],
      mobileDescription:
        'Best for investors building their real estate portfolio one property at a time.',
      mainImage: investmentTemplateData?.earnImage,

      mainImageDescription: {
        offeringName: investmentTemplateData?.earnInvestmentTitle || '',
        offeringLink: investmentTemplateData.earnLink || '',
        metric1: {
          label: investmentTemplateData?.earnInvestmentMetric1Label || '',
          value: investmentTemplateData?.earnInvestmentMetric1Value || '',
        },
        metric2: {
          label: investmentTemplateData?.earnInvestmentMetric2Label || '',
          value: investmentTemplateData?.earnInvestmentMetric2Value || '',
        },
        metric3: {
          label: investmentTemplateData?.earnInvestmentMetric3Label || '',
          value: investmentTemplateData?.earnInvestmentMetric3Value || '',
        },
      },
    },
    {
      investmentType: 'grow',
      kegIconPath: 'icons/grow',
      subHeader:
        'CRE investments offering significant upside potential. Take your real estate portfolio to the next level.',
      listItems: [
        'Value-add and opportunistic equity — target net IRRs of 18%+',
        'Upside-focused funds',
        'Growth-focused portfolios',
      ],
      mobileDescription:
        'Best for investors looking for immediate diversification.',
      mainImage: investmentTemplateData?.growImage,

      mainImageDescription: {
        offeringName: investmentTemplateData?.growInvestmentTitle || '',
        offeringLink: investmentTemplateData.growLink || '',
        metric1: {
          label: investmentTemplateData?.growInvestmentMetric1Label || '',
          value: investmentTemplateData?.growInvestmentMetric1Value || '',
        },
        metric2: {
          label: investmentTemplateData?.growInvestmentMetric2Label || '',
          value: investmentTemplateData?.growInvestmentMetric2Value || '',
        },
        metric3: {
          label: investmentTemplateData?.growInvestmentMetric3Label || '',
          value: investmentTemplateData?.growInvestmentMetric3Value || '',
        },
      },
    },
  ];

  return (
    <>
      {expandedKegData.map((data) => {
        return (
          <div
            id={data.investmentType}
            key={data.investmentType}
            className={styles.desktopExpandedKeg}
          >
            <DesktopExpandedKeg
              mainImage={data.mainImage}
              investmentType={data.investmentType as InvestmentType}
              kegIconPath={
                data.kegIconPath as 'icons/keep' | 'icons/earn' | 'icons/grow'
              }
              subHeader={data.subHeader}
              mainImageDescription={data.mainImageDescription}
              listItems={data.listItems ? data.listItems : []}
              description={data.description ? data.description : ''}
            />
          </div>
        );
      })}
      {expandedKegData.map((data) => {
        return (
          <div
            id={data.investmentType}
            key={data.investmentType}
            className={styles.mobileExpandedKeg}
          >
            <MobileExpandedKeg
              investmentType={data.investmentType as 'keep' | 'earn' | 'grow'}
              mainImage={data.mainImage}
              kegIconPath={
                data.kegIconPath as 'icons/keep' | 'icons/earn' | 'icons/grow'
              }
              subHeader={data.subHeader}
              mainImageDescription={data.mainImageDescription}
              mobileDescription={data.mobileDescription}
            />
          </div>
        );
      })}
    </>
  );
};

export default ExpandedKeg;
